/* eslint formatjs/no-literal-string-in-jsx: "error" */

import React, { FormEvent, useEffect, useState } from "react"
import { useUserContext } from "../../context/UserContext"
import {
  getProviderFinderSearchURL,
  ProviderFinderParam,
  providerFinderRootPath,
  RedirectToProviderFinderURL
} from "../../utils/providerFinderSearchUrl"
import { Redirect, useHistory } from "react-router"
import { getAvailableParentIdsByBenefitConfig } from "../../utils/parentIdMapping"
import { useSearchTypeContext, SearchType } from "../../context/SearchTypeContext"
import { useBroadAreaContext } from "../../context/BroadAreaContext"
import { FormattedMessage, useIntl } from "react-intl"
import {
  Tabs,
  Tab,
  Box,
  Button,
  Stack,
  Tile,
  Typography,
  useTheme,
  FamilyIllustration
} from "@carrotfertility/carotene-core"
import { ProviderTypeFilterDropdown } from "./ProviderTypeFilterDropdown"
import { useGetLocationDetails } from "../../hooks/useGetLocationDetails"
import { LocationPicker } from "../location-picker/LocationPicker"
import { Link as RouterLink } from "react-router-dom"
import { KeywordSearchInput } from "../keyword-search/KeywordSearchInput"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { GarnerProviderType } from "#/pages/provider-finder/utils/garnerProviderTypeMapping"
import { useProviderTypeSearched } from "#/pages/provider-finder/components/results/useProviderTypeSearched"
import { useRadiusUnit } from "#/pages/provider-finder/components/sidebar/RadiusSelect"
import { Paths } from "#/utils/Paths"

export function ProviderSearchForm({
  isCustomNetworks,
  isCarrotCore,
  benefit
}: {
  isCustomNetworks: boolean
  isCarrotCore: boolean
  benefit: Benefit
}): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()

  const [locationPickerStringValue, setLocationPickerStringValue] = useState("")

  const [providerNameValue, setProviderNameValue] = useState("")
  const [providerIdValue, setProviderIdValue] = useState("")

  const [searchLocation, setSearchLocation] = useState("")
  const [providerKeyword, setProviderKeyword] = useState("")
  const [providerId, setProviderId] = useState("")
  const [typeaheadLocationSearch, setTypeaheadLocationSearch] = useState("")

  const { data: locationDetails, isFetching, isError } = useGetLocationDetails(searchLocation)
  const { companyCountryCode } = useUserContext()
  const { setBroadAreaSearched } = useBroadAreaContext()
  const { searchType, setSearchType } = useSearchTypeContext()

  const availableParentIds = getAvailableParentIdsByBenefitConfig(benefit)
  const history = useHistory()
  const params = new URLSearchParams(history.location.search)
  const providerTypeFromParams = params?.get(ProviderFinderParam.ProviderType)?.split(",") ?? []

  // OP-2282 - We now only allow one provider type to be selected at a time but there may still be links in the wild with multiple.
  //           If there are multiple then we won't auto-select any provider type.
  const [selectedProviderType, setSelectedProviderType] = useState(
    availableParentIds.length === 1
      ? availableParentIds[0]
      : providerTypeFromParams.length === 1 && availableParentIds.includes(providerTypeFromParams[0])
      ? providerTypeFromParams[0]
      : ""
  )
  const { isDoulasSearched } = useProviderTypeSearched(selectedProviderType)
  const { radiusUnit } = useRadiusUnit()

  useEffect(() => {
    setBroadAreaSearched(locationDetails?.isBroadArea)
  }, [setBroadAreaSearched, locationDetails?.isBroadArea])

  const handleChange = (_event: any, newValue: SearchType) => {
    setSearchType(newValue)
  }

  const preserveScrollPositionOnNavigation = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    const scrollPosition = window.scrollY
    history.push(event.currentTarget.pathname)
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosition)
    })
  }

  const isSearchDetailsLoaded = searchLocation && !isFetching && !isError
  if (isSearchDetailsLoaded) {
    if (selectedProviderType === GarnerProviderType.OBGYN) {
      return (
        <Redirect
          to={`${providerFinderRootPath}/find-obgyn?location=${encodeURIComponent(searchLocation)}&lat=${
            locationDetails.latitude
          }&long=${locationDetails.longitude}`}
        />
      )
    }
    if (selectedProviderType === GarnerProviderType.PEDIATRICIAN) {
      return (
        <Redirect
          to={`${providerFinderRootPath}/find-pediatrician?location=${encodeURIComponent(searchLocation)}&lat=${
            locationDetails.latitude
          }&long=${locationDetails.longitude}`}
        />
      )
    }

    const searchURL = getProviderFinderSearchURL({
      searchLocation,
      locationDetails,
      companyCountryCode,
      providerType: selectedProviderType,
      radiusInKm: isDoulasSearched ? radiusUnit.getSelectOptionValue(50) : "",
      keyword: providerKeyword,
      providerId
    })

    return <RedirectToProviderFinderURL url={searchURL} />
  }

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault()
    setSearchLocation(locationPickerStringValue)
    setProviderKeyword(providerNameValue)
    setProviderId(providerIdValue)
  }

  if (isError) {
    return <Redirect to={`${providerFinderRootPath}/error`} />
  }

  const color = searchType === SearchType.KEYWORD ? "primary" : "blue"

  return (
    <Tile
      component="section"
      variant="primary"
      illustration={<FamilyIllustration color={color} />}
      slotProps={{ illustrationContainer: { sx: { maxBlockSize: "100%" } } }}
      color={color}
      sx={(theme) => ({
        padding: theme.spacing(theme.tokens.spacing.xl)
      })}
    >
      <Stack direction="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xl)}>
        {!isCarrotCore || isCustomNetworks ? (
          <Tabs
            value={searchType}
            onChange={handleChange}
            indicatorColor="secondary"
            TabIndicatorProps={{
              sx: (theme) => ({
                height: theme.tokens.borderWidth.md,
                borderRadius: theme.tokens.borderRadius.md
              })
            }}
            sx={{
              "& .MuiTabs-flexContainer": {
                borderBlockEnd: "none"
              }
            }}
          >
            <Tab
              component={RouterLink}
              to={Paths.PROVIDER_FINDER}
              label={
                <Typography variant="h5" component="h4" color={(theme) => theme.palette.text.secondary}>
                  <FormattedMessage defaultMessage="Search by type" />
                </Typography>
              }
              value={SearchType.LOCATION}
              sx={(theme) => ({
                padding: theme.tokens.spacing.none
              })}
              onClick={preserveScrollPositionOnNavigation}
            />
            <Tab
              component={RouterLink}
              to={`${Paths.PROVIDER_FINDER}/search-by-name`}
              label={
                <Typography variant="h5" component="h4" color={(theme) => theme.palette.text.secondary}>
                  <FormattedMessage defaultMessage="Search by name" />
                </Typography>
              }
              value={SearchType.KEYWORD}
              sx={(theme) => ({
                padding: theme.tokens.spacing.none,
                marginInlineEnd: theme.tokens.spacing.md,
                marginInlineStart: theme.tokens.spacing.md
              })}
              onClick={preserveScrollPositionOnNavigation}
            />
          </Tabs>
        ) : null}

        <Box
          sx={(theme) => ({
            zIndex: 3,
            inlineSize: "100%",
            [theme.breakpoints.up("md")]: { maxInlineSize: "420px" }
          })}
        >
          <form name="providerSearchForm" onSubmit={handleSubmit}>
            <Stack spacing={theme.tokens.spacing.md}>
              {availableParentIds.length > 1 && (
                <ProviderTypeFilterDropdown
                  selectedProviderType={selectedProviderType}
                  setSelectedProviderType={setSelectedProviderType}
                  benefit={benefit}
                  shouldShowGarnerProviderType={searchType === SearchType.LOCATION}
                />
              )}
              {searchType === SearchType.KEYWORD && (
                <KeywordSearchInput
                  tooltipTitle={
                    <Box>
                      <Typography fontSize="inherit" color={(theme) => theme.palette.secondary.contrastText}>
                        <FormattedMessage defaultMessage="A practice refers to a clinic, law firm, or agency. Generally, our network includes practices, not individuals. Instead of looking for Dr. Smith at CCRM, try looking for the CCRM clinic." />
                      </Typography>
                      <br></br>
                      <Typography fontSize="inherit" color={(theme) => theme.palette.secondary.contrastText}>
                        <FormattedMessage defaultMessage="Sometimes, the practice name is the same as an individual’s name. i.e., John Smith Law Firm." />
                      </Typography>
                    </Box>
                  }
                  label={
                    isDoulasSearched
                      ? intl.formatMessage({ defaultMessage: "Doula's name" })
                      : intl.formatMessage({ defaultMessage: "Practice name" })
                  }
                  setProviderNameValue={setProviderNameValue}
                  setProviderIdValue={setProviderIdValue}
                  setTypeaheadLocationSearch={setTypeaheadLocationSearch}
                  placeholder={
                    isDoulasSearched
                      ? intl.formatMessage({
                          defaultMessage: "Search"
                        })
                      : intl.formatMessage({
                          defaultMessage: "Clinic name, agency, legal service..."
                        })
                  }
                  providerType={selectedProviderType}
                />
              )}
              <LocationPicker
                label={intl.formatMessage({ defaultMessage: "Location" })}
                typeaheadLocationSearch={typeaheadLocationSearch}
                setLocationPickerStringValue={setLocationPickerStringValue}
                isRequired
              />
              <Button
                type="submit"
                size="medium"
                color="secondary"
                variant="contained"
                disabled={
                  searchType === SearchType.KEYWORD
                    ? !locationPickerStringValue || !providerNameValue || !selectedProviderType
                    : !locationPickerStringValue || !selectedProviderType
                }
                sx={{ inlineSize: "fit-content" }}
              >
                <FormattedMessage defaultMessage="Search" />
              </Button>
            </Stack>
          </form>
        </Box>
      </Stack>
    </Tile>
  )
}
